import { useEffect, useState } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import { T } from '../styles/theme';

export default function AdminCorso() {
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showAddModule, setShowAddModule] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(null);
  const [form, setForm] = useState({});
  const toast = useToast();

  useEffect(() => {
    api.get('/auth/me').then((r) => setIsAdmin(r.data.is_admin)).catch(() => {});
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const r = await api.get('/corso/modules');
      setModules(r.data);
      for (const m of r.data) {
        const lr = await api.get(`/corso/modules/${m.id}/lessons`);
        setLessons((prev) => ({ ...prev, [m.id]: lr.data }));
      }
    } catch {}
  };

  const addModule = async () => {
    try {
      await api.post('/corso/modules', { title: form.title || 'Nuovo modulo', description: form.description || '', order_index: modules.length + 1, icon: form.icon || '📚' });
      toast.success('Modulo creato'); setShowAddModule(false); setForm({}); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const deleteModule = async (id) => {
    if (!window.confirm('Eliminare modulo e tutte le lezioni?')) return;
    try { await api.delete(`/corso/modules/${id}`); toast.success('Eliminato'); loadAll(); }
    catch { toast.error('Errore'); }
  };

  const addLesson = async (moduleId) => {
    try {
      await api.post('/corso/lessons', { module_id: moduleId, title: form.title || 'Nuova lezione', content: form.content || '', order_index: (lessons[moduleId]?.length || 0) + 1, duration_minutes: parseInt(form.duration) || 5 });
      toast.success('Lezione creata'); setShowAddLesson(null); setForm({}); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const saveLesson = async () => {
    if (!editingLesson) return;
    try {
      await api.put(`/corso/lessons/${editingLesson.id}`, { title: editingLesson.title, content: editingLesson.content, cover_image: editingLesson.cover_image || '', order_index: editingLesson.order_index, duration_minutes: editingLesson.duration_minutes });
      toast.success('Salvato'); setEditingLesson(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm('Eliminare lezione?')) return;
    try { await api.delete(`/corso/lessons/${id}`); toast.success('Eliminata'); loadAll(); }
    catch { toast.error('Errore'); }
  };

  const openEdit = async (lessonId) => {
    try {
      const r = await api.get(`/corso/lessons/${lessonId}`);
      setEditingLesson(r.data);
    } catch {}
  };

  if (!isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: T.muted, fontFamily: T.fontBody }}>Accesso riservato agli admin</p>
      </div>
    );
  }

  return (
    <Layout>
      <main style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 className="page-title">Admin Corso</h1>
          <button onClick={() => { setShowAddModule(true); setForm({}); }} className="btn-primary">+ Modulo</button>
        </div>

        {showAddModule && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 className="section-title" style={{ marginBottom: 12 }}>Nuovo modulo</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <input placeholder="Titolo" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
              <input placeholder="Descrizione" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" />
              <input placeholder="Icona (emoji)" value={form.icon || ''} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={addModule} className="btn-primary">Crea</button>
              <button onClick={() => setShowAddModule(false)} className="btn-ghost">Annulla</button>
            </div>
          </div>
        )}

        {editingLesson && (
          <div className="card" style={{ marginBottom: 24, borderColor: T.violetDark }}>
            <h3 className="section-title" style={{ marginBottom: 12 }}>Modifica: {editingLesson.title}</h3>
            <input placeholder="Titolo" value={editingLesson.title} onChange={(e) => setEditingLesson({ ...editingLesson, title: e.target.value })} className="input" style={{ marginBottom: 12 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <input placeholder="Cover image URL" value={editingLesson.cover_image || ''} onChange={(e) => setEditingLesson({ ...editingLesson, cover_image: e.target.value })} className="input" />
              <input type="number" placeholder="Durata (min)" value={editingLesson.duration_minutes} onChange={(e) => setEditingLesson({ ...editingLesson, duration_minutes: parseInt(e.target.value) || 5 })} className="input" />
            </div>
            <textarea placeholder="Contenuto (Markdown)" value={editingLesson.content} onChange={(e) => setEditingLesson({ ...editingLesson, content: e.target.value })}
              className="input" style={{ marginBottom: 12, fontFamily: T.fontMono, fontSize: '0.8rem', resize: 'vertical' }} rows={15} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={saveLesson} className="btn-primary">Salva</button>
              <button onClick={() => setEditingLesson(null)} className="btn-ghost">Annulla</button>
            </div>
          </div>
        )}

        {modules.map((m) => (
          <div key={m.id} className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.2rem' }}>{m.icon}</span>
                <span style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.text }}>{m.title}</span>
                <span style={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.muted }}>({m.lesson_count} lezioni)</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowAddLesson(m.id); setForm({}); }} className="btn-ghost" style={{ color: T.green }}>+ Lezione</button>
                <button onClick={() => deleteModule(m.id)} className="btn-ghost" style={{ color: T.red }}>Elimina</button>
              </div>
            </div>

            {showAddLesson === m.id && (
              <div style={{ padding: '12px 20px', background: T.elem, borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, marginBottom: 8 }}>
                  <input placeholder="Titolo lezione" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
                  <input type="number" placeholder="Durata (min)" value={form.duration || ''} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="input" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => addLesson(m.id)} className="btn-primary">Crea</button>
                    <button onClick={() => setShowAddLesson(null)} className="btn-ghost">Annulla</button>
                  </div>
                </div>
                <textarea placeholder="Contenuto (Markdown)" value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} className="input" style={{ fontFamily: T.fontMono, fontSize: '0.8rem' }} rows={5} />
              </div>
            )}

            {(lessons[m.id] || []).map((l) => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: `1px solid rgba(45,31,110,0.3)`, transition: '0.15s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.muted, width: 24, textAlign: 'center' }}>{l.order_index}</span>
                  <span style={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: l.completed ? T.green : T.text }}>{l.title}</span>
                  <span style={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.faint }}>{l.duration_minutes}m</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(l.id)} className="btn-ghost">Modifica</button>
                  <button onClick={() => deleteLesson(l.id)} className="btn-ghost" style={{ color: T.red }}>Elimina</button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </main>
    </Layout>
  );
}
